import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { addYears, isSameDay } from 'date-fns';
import { MenuItem, InlineSelect, Tooltip, Typography, WithInfoIcon, InlineSelectDatePicker } from 'seagull';
import { useIoContext } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { serviceCostModel, serviceModel } from '~/models';
import { DiscountPeriodExpirationModel } from '~/models/DiscountPeriodExpirationModel';
import { isAwaitingApproval, validateDiscountPeriodExpiration, serviceValidationTextsPath } from '~/models/service';
import { billingPeriodTextsPath } from '~/utils/billing-period-text';
import { AdvancedSettingsControl } from '../AdvancedSettingsControl/AdvancedSettingsControl';
import sharedStyles from '../ServiceAdvancedSettings.module.scss';
import { AdvancedSetting } from '../ServiceAdvancedSettings.types';
import { TextAndInlineSelectRow } from '../TextAndInlineSelectRow';
import styles from './DiscountPeriodControl.module.scss';
import { getAmountOptions, getDateRange, PERIOD_OPTIONS } from './DiscountPeriodControl.utils';
function formatPeriodCount(period, count, periodTexts) {
    switch(period){
        case 'days':
            return periodTexts.dayPlural({
                count
            });
        case 'weeks':
            return periodTexts.weekPlural({
                count
            });
        case 'months':
            return periodTexts.monthPlural({
                count
            });
        case 'years':
            return periodTexts.yearPlural({
                count
            });
    }
}
export function DiscountPeriodControl(props) {
    const { service, info, label, isReadOnly, disabled, testId: testPrefix, disabledTooltip, onDiscountPeriodExpirationChange, relationshipState } = props;
    const { t: advTexts } = useLocaleKeys('components.serviceAdvancedSettings');
    const { t: validationTexts } = useLocaleKeys(serviceValidationTextsPath);
    const { t: billingTexts } = useLocaleKeys(billingPeriodTextsPath);
    const { getNow } = useIoContext();
    const now = useMemo(()=>getNow(), []);
    const [selectedDate, setSelectedDate] = useState(()=>addYears(now, 1));
    const isRelationshipApproved = relationshipState.type === 'approved';
    const [timeframeType, setTimeframeType] = useState(isRelationshipApproved ? 'date' : 'period');
    const [periodType, setPeriodType] = useState('years');
    const [amount, setAmount] = useState(1);
    const [error, setError] = useState('');
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const effectiveDate = relationshipState.effectiveDate;
    const cost = serviceModel.cost(service);
    const discountPeriodExpiration = cost.discount ? serviceCostModel.discountPeriodExpiration({
        discount: cost.discount
    }) : undefined;
    if (!isAwaitingApproval(service.status)) {
        return null;
    }
    function getTimeframeType() {
        if (!discountPeriodExpiration) return null;
        return discountPeriodExpiration.type() === 'none' ? null : discountPeriodExpiration.type();
    }
    useOnMount(()=>{
        const type = getTimeframeType();
        if (!type) return;
        setTimeframeType(type);
        const date = discountPeriodExpiration === null || discountPeriodExpiration === void 0 ? void 0 : discountPeriodExpiration.date();
        const period = discountPeriodExpiration === null || discountPeriodExpiration === void 0 ? void 0 : discountPeriodExpiration.period();
        if (type === 'date' && date) {
            setSelectedDate(date);
        } else if (type === 'period' && period) {
            var _ref;
            setPeriodType(period);
            setAmount((_ref = discountPeriodExpiration === null || discountPeriodExpiration === void 0 ? void 0 : discountPeriodExpiration.amount()) !== null && _ref !== void 0 ? _ref : 1);
        }
    });
    useEffect(()=>{
        const error = validateDiscountPeriodExpiration(service, now, validationTexts, effectiveDate);
        setError(error);
        if (discountPeriodExpiration && discountPeriodExpiration.hasExpiration()) {
            const expirationType = discountPeriodExpiration.type();
            if (expirationType === 'date') {
                const date = discountPeriodExpiration.date();
                if (date) {
                    setTimeframeType('date');
                    setSelectedDate(date);
                }
            } else if (expirationType === 'period') {
                const period = discountPeriodExpiration.period();
                const expirationAmount = discountPeriodExpiration.amount();
                if (period && expirationAmount !== undefined) {
                    setTimeframeType('period');
                    setPeriodType(period);
                    setAmount(expirationAmount);
                }
            }
        }
        if (!service.cost.discount) {
            setTimeframeType(isRelationshipApproved ? 'date' : 'period');
            setPeriodType('years');
            setAmount(1);
        }
    }, [
        service,
        now,
        effectiveDate,
        isRelationshipApproved,
        discountPeriodExpiration
    ]);
    const { minDate, maxDate } = useMemo(()=>getDateRange(now, effectiveDate), [
        now,
        effectiveDate
    ]);
    const amountOptions = useMemo(()=>getAmountOptions(periodType), [
        periodType
    ]);
    const isAmountValid = amountOptions.includes(amount);
    const handleChange = (enabled)=>{
        if (!onDiscountPeriodExpirationChange) return;
        if (enabled) {
            const existingDiscount = service.cost.discount;
            if (!existingDiscount) {
                return;
            }
            const expiration = timeframeType === 'date' ? DiscountPeriodExpirationModel.makeDate(selectedDate) : DiscountPeriodExpirationModel.makePeriod(periodType, amount);
            onDiscountPeriodExpirationChange(service.id, expiration);
        } else {
            onDiscountPeriodExpirationChange(service.id, undefined);
        }
    };
    const handleTimeframeTypeChange = (newType)=>{
        if (onDiscountPeriodExpirationChange) {
            const expiration = newType === 'date' ? DiscountPeriodExpirationModel.makeDate(selectedDate) : DiscountPeriodExpirationModel.makePeriod(periodType, amount);
            onDiscountPeriodExpirationChange(service.id, expiration);
        }
        setTimeframeType(newType);
    };
    const handleDateChange = (newDate)=>{
        if (onDiscountPeriodExpirationChange) {
            const expiration = DiscountPeriodExpirationModel.makeDate(newDate);
            onDiscountPeriodExpirationChange(service.id, expiration);
        }
        setSelectedDate(newDate);
    };
    const handleAmountChange = (newAmount)=>{
        if (onDiscountPeriodExpirationChange && timeframeType === 'period') {
            const expiration = DiscountPeriodExpirationModel.makePeriod(periodType, newAmount);
            onDiscountPeriodExpirationChange(service.id, expiration);
        }
        setAmount(newAmount);
    };
    const handlePeriodTypeChange = (newPeriodType)=>{
        if (onDiscountPeriodExpirationChange && timeframeType === 'period') {
            const expiration = DiscountPeriodExpirationModel.makePeriod(newPeriodType, amount);
            onDiscountPeriodExpirationChange(service.id, expiration);
        }
        setPeriodType(newPeriodType);
    };
    const shouldShowDefaultView = disabled || (discountPeriodExpiration === null || discountPeriodExpiration === void 0 ? void 0 : discountPeriodExpiration.isNone()) || !discountPeriodExpiration;
    const adjustedMinDate = useMemo(()=>{
        return selectedDate < minDate ? selectedDate : minDate;
    }, [
        selectedDate,
        minDate
    ]);
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        info: shouldShowDefaultView ? info : '',
        infoTestId: "".concat(testPrefix, "-discount-period-info"),
        disabled: disabled,
        label: shouldShowDefaultView ? label : '',
        isReadOnly: isReadOnly,
        testId: "".concat(testPrefix, "-discount-period"),
        bi: {
            name: 'discount_expiration_enabled'
        },
        disabledTooltip: shouldShowDefaultView ? disabledTooltip : '',
        onChange: handleChange,
        checked: !disabled && !!(discountPeriodExpiration === null || discountPeriodExpiration === void 0 ? void 0 : discountPeriodExpiration.hasExpiration()),
        labelSuffix: !shouldShowDefaultView && /*#__PURE__*/ _jsx(WithInfoIcon, {
            className: styles.rowAndIconContainer,
            info: info,
            infoTestId: "".concat(testPrefix, "-discount-period-info"),
            children: /*#__PURE__*/ _jsxs(TextAndInlineSelectRow, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: isRelationshipApproved ? advTexts.expireDiscountOn() : advTexts.expireDiscount()
                    }),
                    timeframeType === 'date' ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !isRelationshipApproved && /*#__PURE__*/ _jsxs(InlineSelect, {
                                skin: "primary",
                                disabled: disabled,
                                value: timeframeType,
                                testId: "".concat(testPrefix, "-timeframe-type"),
                                bi: {
                                    name: 'discount_expiration_timeframe_type'
                                },
                                onChange: (e)=>handleTimeframeTypeChange(e.target.value),
                                SelectProps: {
                                    renderValue: ()=>advTexts.onRenderValue()
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(MenuItem, {
                                        value: "date",
                                        children: advTexts.onASpecificDate()
                                    }),
                                    /*#__PURE__*/ _jsx(MenuItem, {
                                        value: "period",
                                        children: advTexts.setTimeAfterEffectiveDate()
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: isCalendarOpen ? '' : error,
                                placement: "top",
                                children: /*#__PURE__*/ _jsx("div", {
                                    style: {
                                        position: 'relative'
                                    },
                                    onFocus: ()=>setIsCalendarOpen(true),
                                    onBlur: ()=>setIsCalendarOpen(false),
                                    children: /*#__PURE__*/ _jsx(InlineSelectDatePicker, {
                                        bi: {
                                            name: 'discount_deadline_date'
                                        },
                                        size: "small",
                                        testId: "".concat(testPrefix, "-deadline-date"),
                                        className: sharedStyles.noShrink,
                                        value: selectedDate,
                                        minDate: adjustedMinDate,
                                        maxDate: maxDate,
                                        disabled: disabled,
                                        disableErrors: !error,
                                        error: !!error,
                                        skin: "primary",
                                        format: "MMM d, yyyy",
                                        onChange: (newDate)=>{
                                            if (newDate && newDate < minDate) {
                                                handleDateChange(minDate);
                                            } else if (newDate) {
                                                handleDateChange(newDate);
                                            }
                                        },
                                        shouldDisableDate: (date)=>!!date && (date < minDate || date > maxDate) && !isSameDay(date, selectedDate)
                                    })
                                })
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Tooltip, {
                                title: error,
                                placement: "top",
                                disableArrow: true,
                                testId: "".concat(testPrefix, "-amount-select-tooltip"),
                                children: /*#__PURE__*/ _jsxs(InlineSelect, {
                                    skin: "primary",
                                    disabled: disabled,
                                    value: amount.toString(),
                                    testId: "".concat(testPrefix, "-amount-select"),
                                    bi: {
                                        name: 'discount_expiration_amount'
                                    },
                                    onChange: (e)=>handleAmountChange(Number(e.target.value)),
                                    className: styles.amountSelect,
                                    error: !isAmountValid,
                                    children: [
                                        amountOptions.map((value)=>/*#__PURE__*/ _jsx(MenuItem, {
                                                value: value.toString(),
                                                testId: "".concat(testPrefix, "-amount-option-").concat(value),
                                                children: value
                                            }, value)),
                                        !isAmountValid && /*#__PURE__*/ _jsx(MenuItem, {
                                            value: amount.toString(),
                                            disabled: true,
                                            style: {
                                                display: 'none'
                                            },
                                            children: amount
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(InlineSelect, {
                                skin: "primary",
                                disabled: disabled,
                                value: periodType,
                                testId: "".concat(testPrefix, "-period-select"),
                                bi: {
                                    name: 'discount_expiration_period'
                                },
                                onChange: (e)=>handlePeriodTypeChange(e.target.value),
                                children: PERIOD_OPTIONS.map((period)=>/*#__PURE__*/ _jsx(MenuItem, {
                                        value: period,
                                        testId: "".concat(testPrefix, "-period-type-option-").concat(period),
                                        children: formatPeriodCount(period, amount, billingTexts.periods)
                                    }, period))
                            }),
                            /*#__PURE__*/ _jsxs(InlineSelect, {
                                bi: {
                                    name: 'inline_discount_timeframe_type'
                                },
                                skin: "primary",
                                disabled: disabled,
                                value: timeframeType,
                                testId: "".concat(testPrefix, "-timeframe-type"),
                                onChange: (e)=>handleTimeframeTypeChange(e.target.value),
                                SelectProps: {
                                    renderValue: ()=>advTexts.afterEffectiveDateRenderValue()
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(MenuItem, {
                                        value: "date",
                                        children: advTexts.onASpecificDate()
                                    }),
                                    /*#__PURE__*/ _jsx(MenuItem, {
                                        value: "period",
                                        children: advTexts.setTimeAfterEffectiveDate()
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        })
    }, AdvancedSetting.LimitedTimeDiscountOfferForAgreement);
}
